import { Injectable, signal, WritableSignal } from '@angular/core';

import { isTouchDevice } from '../utils';

// ignoredKeyCodes =     /?   ;  Shift Caps
const IGNORED_BARCODE_KEYCODES = [187, 191, 186, 16, 20];

declare type ReaderServiceCallback = (data: KeyboardEvent) => boolean;

declare interface ReaderServiceCallbackTypeMap {
    isBarcode: boolean;
    callback: ReaderServiceCallback;
}

export const keycodes = {
    backspace: 8,
    tab: 9,
    enter: 13,
    shift: 16,
    ctrl: 17,
    alt: 18,
    pause_break: 19,
    caps_lock: 20,
    escape: 27,
    space: 32,
    page_up: 33,
    page_down: 34,
    end: 35,
    home: 36,
    left_arrow: 37,
    up_arrow: 38,
    right_arrow: 39,
    down_arrow: 40,
    insert: 45,
    delete: 46,
    f1: 112,
    f2: 113,
    f3: 114,
    f4: 115,
    f5: 116,
    f6: 117,
    f7: 118,
    f8: 119,
    f9: 120,
    f10: 121,
    f11: 122,
    f12: 123,
    num_lock: 144,
    scroll_lock: 145,
};

@Injectable({ providedIn: 'root' })
export class BarcodeScannerService {
    isTouch: boolean = isTouchDevice();
    disabled: WritableSignal<boolean> = signal(false);

    constructor() {}
    callbacks: ReaderServiceCallbackTypeMap[] = [];

    listen = (handler: any, isBarcode = false) => {
        const buffer: any = [];
        const threshold: any = this.isTouch ? 120 : 60; // ms
        let timer: any = null;

        function notify() {
            const input = buffer.join('').trim();

            clearTimeout(timer);
            buffer.length = 0;
            handler(input);
        }

        const callback = (event: KeyboardEvent) => {
            if (timer) {
                clearTimeout(timer);
            }

            if (this.disabled() === false) {
                timer = setTimeout(notify, threshold);
            }

            // Normalize Enter detection across key events
            const isEnter = event.key === 'Enter' || event.code === 'Enter' || event.keyCode === keycodes.enter;
            if (isEnter) {
                event.stopImmediatePropagation();
                // flush immediately
                notify();
                return false;
            }

            if (isBarcode) {
                // ignore modifier keys or other configured ignored codes
                if (IGNORED_BARCODE_KEYCODES.indexOf(event.keyCode) >= 0) {
                    return;
                }

                // Prefer event.code (physical key) to be layout-independent.
                // map Digit0..Digit9 and Numpad0..Numpad9 to their numeric characters.
                let digit: string | null = null;

                if (event.code && typeof event.code === 'string') {
                    if (event.code.startsWith('Digit')) {
                        digit = event.code.slice(5); // 'Digit1' -> '1'
                    } else if (event.code.startsWith('Numpad')) {
                        const np = event.code.slice(6); // 'Numpad1' -> '1'
                        if (/^\d$/.test(np)) {
                            digit = np;
                        }
                    }
                }

                // Fallback to event.key (character) if code wasn't available or mapped.
                if (digit === null) {
                    if (typeof event.key === 'string' && /^\d$/.test(event.key)) {
                        digit = event.key;
                    } else if (typeof (event as any).charCode === 'number' && (event as any).charCode > 0) {
                        const ch = String.fromCharCode((event as any).charCode);
                        if (/^\d$/.test(ch)) {
                            digit = ch;
                        }
                    }
                }

                if (digit !== null) {
                    buffer.push(digit);
                } else {
                    // non-digit encountered -> reset buffer
                    buffer.length = 0;
                }
            } else {
                // non-barcode: push printable key value
                let ch: string;
                if (typeof event.key === 'string' && event.key.length === 1) {
                    ch = event.key;
                } else {
                    ch = String.fromCharCode((event as any).charCode || (event as any).keyCode || 0);
                }
                buffer.push(ch);
            }
        };
        if (this.callbacks.length) {
            const lastCallback = this.callbacks[this.callbacks.length - 1].callback;
            document.body.removeEventListener('keydown', lastCallback);
        }
        this.callbacks.push({ callback, isBarcode });
        document.body.addEventListener('keydown', callback);
    };

    destroy() {
        const lastCallbackItem = this.callbacks.pop();

        document.body.removeEventListener('keydown', lastCallbackItem?.callback);
        if (this.callbacks.length) {
            const lastCallback = this.callbacks[this.callbacks.length - 1];
            document.body.addEventListener('keydown', lastCallback?.callback);
        }
    }
}
